import React, { useMemo } from "react";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import { setUrlParam } from "../../../../utils/urlParams";
import { navigate } from "../../../shared/LinkComponent";
import Button, { ButtonColor } from "../../../shared/NewForms/Button";
import Headline from "../../../shared/NewHeadline";
import defaultImg from "../Img/platzhalter_technikshop.png";
import useStore, { getGroupName } from "../store";

const Container = styled.div``;

const Links = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    margin-top: 40px;
`;

const PageMeta = styled.div`
    margin-right: 20px;
    margin-left: 20px;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: repeat(2, 1fr);
       grid-gap: 70px;
    `}
    ${({ theme }) => theme.breakpoints.from.normal.css`
       grid-template-columns: repeat(3, 1fr);
       grid-gap: 70px;
    `}

    margin-top: 20px;
`;

const ProductItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    justify-content: space-between;
`;

const ProductName = styled.div`
    font-size: 1.1em;
    font-weight: 500;
`;

const ProductStatsContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Image = styled.img`
    object-fit: contain;
    width: 100%;
    height: 250px;
`;

const LoadingArtikel = styled.div`
    width: 100%;
    margin: 10px;
    background: white;
    border-radius: 5px;
    box-shadow: 0 0 10px rgb(0 0 0 / 5%);

    .content {
        padding: 20px 0;
    }

    .image,
    .headline,
    .text,
    .button {
        background: #eee;
        background: linear-gradient(
            110deg,
            #ececec 8%,
            whitesmoke 18%,
            #ececec 33%
        );
        background-size: 200% 100%;
        border-radius: 5px;
        animation: 1.5s shine linear infinite;
    }

    .image {
        height: 200px;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
    }

    .headline {
        height: 30px;
        margin: 5px 0;
    }

    .text {
        height: 50px;
        margin: 5px 0;
    }

    .button {
        height: 20px;
        margin: 5px 0;
    }
    @keyframes shine {
        to {
            background-position-x: -200%;
        }
    }
`;

const ProductList = () => {
    const [
        loading,
        products,
        productGroups,
        selectedProductGroupId,
        paginationLinks,
        paginationMeta,
        setPage,
        getMainImage,
        getData,
    ] = useStore(
        (state) => [
            state.loading,
            state.products,
            state.productGroups,
            state.selectedProductGroupId,
            state.paginationLinks,
            state.paginationMeta,
            state.setPage,
            state.getMainImage,
            state.getData,
        ],
        shallow
    );

    const groupName = useMemo(() => {
        if (selectedProductGroupId === null) {
            return "Suchergebnis";
        }
        return getGroupName(productGroups, selectedProductGroupId);
    }, [productGroups]);

    const changeLocation = (param: string) => {
        const parts = param.replace("?", "").split("=");
        setUrlParam(parts[0], parts[1]);
        setPage(parseInt(parts[1]));
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        getData();
    };

    return (
        <Container>
            <Headline>{groupName}</Headline>
            <Grid>
                {!loading &&
                    (products.length >= 1 ? (
                        products.map((product) => (
                            <ProductItem key={product.articleNumber}>
                                <div
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                    `}
                                >
                                    <Image
                                        src={
                                            getMainImage(product) ?? defaultImg
                                        }
                                        alt={product.name}
                                    />
                                    <ProductName>{product.name}</ProductName>
                                </div>

                                <div
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                    `}
                                >
                                    <ProductStatsContainer>
                                        <div></div>
                                        <div>
                                            {product.listPrice
                                                ? `${product.listPrice} €`
                                                : "Preis auf Anfrage"}
                                        </div>
                                    </ProductStatsContainer>
                                    <ProductStatsContainer>
                                        <div></div>
                                        <div>
                                            {product.quantityUnit
                                                .toString()
                                                .replace(".00", "")}{" "}
                                            {product.packagingUnit}
                                        </div>
                                    </ProductStatsContainer>
                                    <Button
                                        fullWidth
                                        onClick={() =>
                                            navigate(
                                                `technik/product/${product.articleNumber}`
                                            )
                                        }
                                    >
                                        Mehr erfahren
                                    </Button>
                                </div>
                            </ProductItem>
                        ))
                    ) : (
                        <p>keine Produkte gefunden</p>
                    ))}
                {loading && (
                    <>
                        {[...Array(3)].map((_, index) => (
                            <LoadingArtikel key={index}>
                                <div className="image"></div>
                                <div className="content">
                                    <div className="headline"></div>
                                    <div className="text"></div>
                                    <div className="button"></div>
                                </div>
                            </LoadingArtikel>
                        ))}
                    </>
                )}
            </Grid>
            {paginationLinks && paginationMeta && (
                <Links>
                    <Button
                        extraSmall
                        color={ButtonColor.blueOutline}
                        onClick={() => changeLocation(paginationLinks.first)}
                    >
                        {"<<"}
                    </Button>
                    <Button
                        disabled={paginationLinks.prev === null}
                        extraSmall
                        color={ButtonColor.blueOutline}
                        onClick={() => {
                            if (paginationLinks.prev !== null) {
                                changeLocation(paginationLinks.prev);
                            }
                        }}
                    >
                        {"<"}
                    </Button>
                    <PageMeta>
                        Seite {paginationMeta.current_page} von{" "}
                        {paginationMeta.last_page}
                    </PageMeta>
                    <Button
                        disabled={paginationLinks.next === null}
                        extraSmall
                        color={ButtonColor.blueOutline}
                        onClick={() => {
                            if (paginationLinks.next !== null) {
                                changeLocation(paginationLinks.next);
                            }
                        }}
                    >
                        {">"}
                    </Button>
                    <Button
                        extraSmall
                        color={ButtonColor.blueOutline}
                        onClick={() => changeLocation(paginationLinks.last)}
                    >
                        {">>"}
                    </Button>
                </Links>
            )}
        </Container>
    );
};

export default ProductList;
